import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ConfiguratorState } from '../../types';
import {
  getFixedGlassTypes,
  getMinHeightForGlassTypeSafe,
} from '../../config/priceConfigFixed';
import { SKYVISION_CIRCULAR_CONFIG } from '../../config/priceConfigCircular';
import { SKYVISION_LINEAR_CONFIG } from '../../config/priceConfigLinear';
import { SKYVISION_COMFORT_CONFIG } from '../../config/priceConfigComfort';
import { SKYVISION_WALKON_CONFIG } from '../../config/priceConfigWalkOn';
import { SKYVISION_ACCESS_CONFIG } from '../../config/priceConfigAccess';
import PerspectiveViewer, {
  linearViewerDimensions,
  productKindFromName,
} from '../UI/PerspectiveViewer';
import { debugError } from '../../utils/debug';
import { formatDisplayPrice } from '../../utils/priceFormat';
import { replaceUkTerm } from '../../utils/summaryHelpers';
import { productCodeFromName } from '../../services/priceService';

interface GlassTypeProps {
  state: ConfiguratorState;
  updateState: (updates: Partial<ConfiguratorState>) => void;
  onNext: () => void;
  onBack?: () => void;
  showGross?: boolean;
}

interface GlazingType {
  id: string;
  glazing_code?: string;
  name: string;
  description: string;
  glassLayers?: number;
  substructureHeight?: number;
  minHeight?: number;
  uValue?: number;
  surchargePercentage?: number;
  surchargeFixed?: number;
  surcharge?: number;
  surchargeType?: 'none' | 'percentage' | 'fixed' | 'formula';
  surchargeFormula?: string;
  formulaVariables?: Record<string, string>;
  infoText?: string;
}

/** Copy the surcharge signal into a compact price-label string. */
function surchargeLabel(g: GlazingType, showGross: boolean): string {
  if (
    g.surchargeType === 'none' ||
    (!g.surchargeType && !g.surchargePercentage && !g.surchargeFixed)
  ) {
    return 'Inklusive';
  }
  if (g.surchargeType === 'formula') return '+ Aufpreis';
  if (g.surchargeType === 'percentage' && g.surchargePercentage && g.surchargePercentage > 0) {
    return `+ ${g.surchargePercentage} %`;
  }
  if (g.surchargeType === 'fixed' && g.surchargeFixed && g.surchargeFixed > 0) {
    return `+ ${formatDisplayPrice(g.surchargeFixed, showGross, 0)}`;
  }
  if (g.surchargePercentage && g.surchargePercentage > 0) return `+ ${g.surchargePercentage} %`;
  if (g.surchargeFixed && g.surchargeFixed > 0) {
    return `+ ${formatDisplayPrice(g.surchargeFixed, showGross, 0)}`;
  }
  return 'Inklusive';
}

/**
 * Produkt-spezifische Verglasungs-Fallbacks falls die REST-API nicht
 * erreichbar ist. Verhalten identisch zur Vor-Direction-A-Version —
 * nur die UI hat sich geändert.
 */
function getFallbackGlazingTypes(productName: string): GlazingType[] {
  const walkable =
    productName === 'SkyVision WALKON' ||
    productName === 'SkyVision LINEAR WALK-ON' ||
    productName === 'SkyVision CIRCULAR WALK-ON';
  if (walkable) {
    const source = SKYVISION_WALKON_CONFIG.glassTypes;
    return (['double', 'triple', 'none'] as const).map((id) => ({
      id,
      name: source[id].name,
      description: source[id].description,
      minHeight: source[id].minHeight,
    }));
  }
  let source: {
    double: { name: string; description: string; minHeight?: number };
    triple: { name: string; description: string; minHeight?: number };
  };
  switch (productName) {
    case 'SkyVision COMFORT':
      source = SKYVISION_COMFORT_CONFIG.glassTypes;
      break;
    case 'SkyVision CIRCULAR':
      source = SKYVISION_CIRCULAR_CONFIG.glassTypes;
      break;
    case 'SkyVision LINEAR':
      source = SKYVISION_LINEAR_CONFIG.glassTypes;
      break;
    case 'SkyVision ACCESS':
      source = SKYVISION_ACCESS_CONFIG.glassTypes;
      break;
    default:
      source = getFixedGlassTypes();
  }
  return [
    {
      id: 'double',
      name: source.double.name,
      description: source.double.description,
      minHeight: source.double.minHeight,
    },
    {
      id: 'triple',
      name: source.triple.name,
      description: source.triple.description,
      minHeight: source.triple.minHeight,
    },
  ];
}

export default function GlassType({
  state,
  updateState,
  onNext,
  onBack,
  showGross = true,
}: GlassTypeProps) {
  const [glazingTypes, setGlazingTypes] = useState<GlazingType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usingFallback, setUsingFallback] = useState(false);
  const radioRefs = useRef<Array<HTMLDivElement | null>>([]);

  const loadGlazingTypes = useCallback(async () => {
    if (!state.product) {
      setLoading(false);
      setGlazingTypes([]);
      setError(null);
      setUsingFallback(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      setUsingFallback(false);
      const productId = productCodeFromName(state.product);
      const response = await fetch(
        `/wp-json/skyvision/v1/products/${productId}/glazing-types`,
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (!Array.isArray(data) || data.length === 0) {
        throw new Error('Keine Verglasungstypen vom Server erhalten');
      }
      setGlazingTypes(data);
    } catch (err) {
      debugError('Fehler beim Laden der Verglasungstypen:', err);
      setError(
        'Verglasungsoptionen konnten nicht geladen werden. Es werden vorläufige Standardwerte angezeigt.',
      );
      setGlazingTypes(getFallbackGlazingTypes(state.product));
      setUsingFallback(true);
    } finally {
      setLoading(false);
    }
  }, [state.product]);

  useEffect(() => {
    loadGlazingTypes();
  }, [loadGlazingTypes]);

  const handleGlassTypeChange = (
    type: string,
    glazingType?: GlazingType,
  ) => {
    const newDimensions = { ...state.dimensions };
    const minHeight = glazingType?.substructureHeight || getMinHeightForGlassTypeSafe(
      type === 'triple' ? 'triple' : 'double',
    );

    if (
      state.product === 'SkyVision FIXED' ||
      state.product === 'SkyVision COMFORT' ||
      state.product === 'SkyVision ACCESS' ||
      state.product === 'SkyVision WALKON' ||
      state.product === 'SkyVision LINEAR WALK-ON' ||
      state.product === 'SkyVision CIRCULAR WALK-ON'
    ) {
      const currentMinHeight = state.glassType === 'triple' ? 240 : 210;
      if (newDimensions.height < minHeight) newDimensions.height = minHeight;
      else if (newDimensions.height === currentMinHeight) newDimensions.height = minHeight;
    }

    updateState({
      glassType: type,
      dimensions: newDimensions,
      glazingInfo: glazingType
        ? {
            surchargeType: glazingType.surchargeType,
            surchargePercentage: glazingType.surchargePercentage,
            surchargeFixed: glazingType.surchargeFixed,
            surchargeFormula: glazingType.surchargeFormula,
            name: glazingType.name,
          }
        : undefined,
    });
  };

  // ---- Guard: missing product context (e.g. deep-link) ----
  if (!state.product) {
    return (
      <div className="py-16 max-w-lg">
        <p className="sv-caps text-[11px] text-ink-3 mb-3">Kein Produkt gewählt</p>
        <p className="sv-serif text-ink-2 mb-4">
          Bitte wählen Sie zunächst ein Produkt auf Blatt 1 aus, damit
          wir die passenden Verglasungen anzeigen können.
        </p>
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="sv-caps-sm text-[11px] text-ink underline decoration-rubric decoration-2 underline-offset-4"
          >
            ← Zurück zur Produktauswahl
          </button>
        )}
      </div>
    );
  }

  return (
    <div>
      <h2 className="sr-only">Verglasung auswählen</h2>

      {/* Direction-A layout: options on the left, drawing on the right. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-start">
        {/* -------- LEFT: Glazing options -------- */}
        <div>
          <p className="sv-lede mb-8">
            {state.product === 'SkyVision WALKON' ||
            state.product === 'SkyVision LINEAR WALK-ON' ||
            state.product === 'SkyVision CIRCULAR WALK-ON'
              ? '2-fach, 3-fach oder ohne Isolierglas. Die Wahl ändert U-Wert, Glaspreis und die Höhe des Aufsatzkranzes.'
              : 'Zwei Verglasungspakete. Die Wahl beeinflusst U-Wert, Aufpreis und die Höhe des Aufsatzkranzes.'}
          </p>

          {error && (
            <div className="mb-6 sv-hairline p-4 bg-paper-2">
              <p className="sv-serif text-[12.5px] text-ink-2">{error}</p>
              {usingFallback && (
                <p className="mt-2 sv-mono text-[11px] text-ink-3">
                  Werte vorläufig — final vom Server bestätigt.
                </p>
              )}
              <button
                type="button"
                onClick={loadGlazingTypes}
                className="mt-3 sv-caps-sm text-[11px] text-rubric underline decoration-rubric decoration-1 underline-offset-4"
              >
                Erneut versuchen
              </button>
            </div>
          )}

          {loading ? (
            <div
              className="sv-hairline p-6 min-h-[120px] flex items-center sv-caps-sm text-[11px] text-ink-3"
              aria-busy="true"
            >
              Verglasungen werden geladen …
            </div>
          ) : glazingTypes.length === 0 ? (
            <p className="sv-serif text-ink-2">
              Für dieses Produkt sind aktuell keine Verglasungen
              hinterlegt.
            </p>
          ) : (
            <div
              role="radiogroup"
              aria-label="Verglasung auswählen"
              className="sv-acc-grid"
            >
              {glazingTypes.map((g, idx) => {
                const isSelected = state.glassType === g.id;
                const selectedIdx = glazingTypes.findIndex(
                  (x) => x.id === state.glassType,
                );
                const tabbableIdx = selectedIdx >= 0 ? selectedIdx : 0;
                const tabIndex = idx === tabbableIdx ? 0 : -1;

                const activate = () =>
                  handleGlassTypeChange(g.id, g);

                const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
                  const total = glazingTypes.length;
                  let nextIdx = idx;
                  switch (e.key) {
                    case ' ':
                    case 'Enter':
                      e.preventDefault();
                      activate();
                      return;
                    case 'ArrowDown':
                    case 'ArrowRight':
                      e.preventDefault();
                      nextIdx = (idx + 1) % total;
                      break;
                    case 'ArrowUp':
                    case 'ArrowLeft':
                      e.preventDefault();
                      nextIdx = (idx - 1 + total) % total;
                      break;
                    case 'Home':
                      e.preventDefault();
                      nextIdx = 0;
                      break;
                    case 'End':
                      e.preventDefault();
                      nextIdx = total - 1;
                      break;
                    default:
                      return;
                  }
                  const nextG = glazingTypes[nextIdx];
                  radioRefs.current[nextIdx]?.focus();
                  handleGlassTypeChange(nextG.id, nextG);
                };

                return (
                  <div
                    key={g.id}
                    ref={(el) => {
                      radioRefs.current[idx] = el;
                    }}
                    role="radio"
                    aria-checked={isSelected}
                    aria-label={g.name}
                    tabIndex={tabIndex}
                    onClick={activate}
                    onKeyDown={handleKeyDown}
                    className={`sv-option-card ${isSelected ? 'is-selected' : ''}`}
                  >
                    <div className="sv-option-card-top">
                      <h3 className="sv-option-card-name">{g.name}</h3>
                      <span className="sv-option-card-price">{surchargeLabel(g, showGross)}</span>
                    </div>
                    <p className="sv-option-card-desc">{replaceUkTerm(g.description)}</p>
                    {g.minHeight &&
                      (state.product === 'SkyVision FIXED' ||
                        state.product === 'SkyVision COMFORT' ||
                        state.product === 'SkyVision ACCESS' ||
                        state.product === 'SkyVision WALKON' ||
                        state.product === 'SkyVision LINEAR WALK-ON' ||
                        state.product === 'SkyVision CIRCULAR WALK-ON') && (
                        <span className="sv-option-card-meta">
                          Mindesthöhe Aufsatzkranz · {g.minHeight} mm
                          {g.surchargeType === 'formula' ? ' · größerer Aufsatzkranz' : ''}
                        </span>
                      )}
                    {isSelected && (
                      <span className="sv-option-card-meta">gewählt</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* -------- RIGHT: live drawing -------- */}
        <div>
          <PerspectiveViewer
            kind={productKindFromName(state.product)}
            productKey={state.product}
            dimensions={
              state.product === 'SkyVision LINEAR' ||
              state.product === 'SkyVision LINEAR WALK-ON'
                ? linearViewerDimensions(state.dimensions, state.modules)
                : {
                    width: state.dimensions.width || 1200,
                    length: state.dimensions.length || 1200,
                    height: state.dimensions.height || 210,
                    diameter: state.dimensions.diameter,
                  }
            }
            selected
            size="lg"
            initialView="photo"
          />
        </div>
      </div>
    </div>
  );
}
